import * as Immutable from 'immutable';
import * as t from 'io-ts';

import { EditorTab as EditorTabType } from '../../reducers/EditorReducer/EditorReducer.types';
import { Choice, Choices, DropdownOption } from '../../components/Table/tableFilter.types';

import { convertArrayFromDropdown, convertArrayFromDropdownProperties } from './index';
import { ioTsValidationError } from './fetch-helper';

export const checkIfInteger = (arg: string) => {
  if (arg === undefined) {
    return false;
  } else {
    return arg.search(/^\d+$/) === -1 ? false : true;
  }
};

export const checkIfFloat = (arg: string) => {
  if (arg === undefined) {
    return false;
  } else {
    return arg.search(/^[+-]?([0-9]*[.])?[0-9]*$/) === -1 ? false : true;
  }
};

export const checkIfEmail = (str: string) => {
  if (str === undefined) {
    return false;
  } else {
    const regex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;

    return regex.test(str);
  }
};

export const checkDateISO8601 = (str: string) => {
  const regex = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(([+-]\d\d:\d\d)|Z)?$/i;

  return regex.test(str);
};

export const checkIfIsValid = (data: any, validation: any) => {
  if (Array.isArray(data)) {
    const invalidData = data.filter(info => !t.exact(validation).decode(info).isRight());
    try {
      console.error(invalidData?.[0]);
      ioTsValidationError(invalidData?.[0], validation);
    } catch (e) {
      console.error(e);
    }

    return invalidData.length === 0;
  } else {
    const decode = t.exact(validation).decode(data);
    try {
      if (!decode.isRight()) {
        console.error(decode);
        ioTsValidationError(data, validation);
      }
    } catch (e) {
      console.error(e);
    }

    return decode.isRight();
  }
};

export const checkInterval = (value: number, min: number, max: number, fieldMessage: string = 'Value') => {
  let messages = Immutable.List<string>();
  if (value < min) {
    messages = messages.push(`${fieldMessage}  ${value} should be greater or equal to ${min}`);
  } else if (value > max) {
    messages = messages.push(`${fieldMessage} ${value} should be smaller or equal to ${max}`);
  } else if (value >= min && value <= max) {
    return messages;
  } else {
    messages = messages.push(`${fieldMessage} ${value} should be a number between ${min} and ${max}`);
  }

  return messages;
};

export const checkSettings = (tabActive: EditorTabType, truncationInterval: number[], type: string) => {
  const childTabs = tabActive.get('childTabs');
  let messages = Immutable.List<string>();
  if (tabActive.has('childTabs')) {
    childTabs.map((tab: EditorTabType) => {
      const settings = tab.get('settings');
      const tabNameFather = tabActive.get('tabName');
      const tabNameChild = tab.get('tabName');
      if (!checkIfInteger(settings.getIn(['decay', 'value']))) {
        messages = messages.push(`Wrong value for parameter decay in ${tabNameFather} , ${tabNameChild}`);
      }
      if (!checkIfFloat(settings.getIn(['truncation', 'value']))) {
        messages = messages.push(`Wrong value for parameter Truncation in ${tabNameFather} , ${tabNameChild}`);
      }
      if (tab.get('code').trim().length === 0) {
        messages = messages.push(`Tab ${tabNameFather} , ${tabNameChild} does not have code`);
      }

      if (truncationInterval !== undefined) {
        const intervalErrors = checkInterval(
          settings.getIn(['truncation', 'value']),
          truncationInterval[0],
          truncationInterval[1],
          'The settings truncation is set to'
        );
        if (intervalErrors.size > 0) {
          messages = messages.push(intervalErrors.first());
        }
      }
    });
  } else if (type === 'SUPER') {
    const settings = tabActive.get('settings');
    const tabNameFather = tabActive.get('tabName');
    if (!checkIfInteger(settings.getIn(['decay', 'value']))) {
      messages = messages.push(`Wrong value for parameter decay in ${tabNameFather}`);
    }
    if (!checkIfFloat(settings.getIn(['truncation', 'value']))) {
      messages = messages.push(`Wrong value for parameter Truncation in ${tabNameFather}`);
    }
    if (truncationInterval !== undefined) {
      const intervalErrors = checkInterval(
        settings.getIn(['truncation', 'value']),
        truncationInterval[0],
        truncationInterval[1],
        'The settings truncation is set to'
      );
      if (intervalErrors.size > 0) {
        messages = messages.push(intervalErrors.first());
      }
    }
    if (tabActive.get('combo').trim().length === 0) {
      messages = messages.push(`Tab ${tabNameFather} does not have combo expression`);
    }
    if (tabActive.get('selection').trim().length === 0) {
      messages = messages.push(`Tab ${tabNameFather} does not have Selection expression`);
    }
  } else {
    const settings = tabActive.get('settings');
    const tabNameFather = tabActive.get('tabName');
    if (!checkIfInteger(settings.getIn(['decay', 'value']))) {
      messages = messages.push(`Wrong value for parameter decay in ${tabNameFather}`);
    }
    if (!checkIfFloat(settings.getIn(['truncation', 'value']))) {
      messages = messages.push(`Wrong value for parameter Truncation in ${tabNameFather}`);
    }
    if (truncationInterval !== undefined) {
      const intervalErrors = checkInterval(
        settings.getIn(['truncation', 'value']),
        truncationInterval[0],
        truncationInterval[1],
        'The settings truncation is set to'
      );
      if (intervalErrors.size > 0) {
        messages = messages.push(intervalErrors.first());
      }
    }
    if (tabActive.get('code') && tabActive.get('code').trim().length === 0) {
      messages = messages.push(`Tab ${tabNameFather} does not have code`);
    }
  }

  return messages;
};

export const checkSettingsWithoutCode = (tabActive: EditorTabType, options: any) => {
  const childTabs = tabActive.get('childTabs');
  let messages = Immutable.List<string>();
  const settingsFather = tabActive.get('settings');
  const currentInstrumentType =
    settingsFather.getIn(['instrumentType', 'value']) && settingsFather.getIn(['instrumentType', 'value']).trim().length > 0
      ? settingsFather.getIn(['instrumentType', 'value'])
      : 'EQUITY';

  const path = ['choices', 'instrumentType', currentInstrumentType, 'region', settingsFather.getIn(['region', 'value'])];

  if (tabActive.has('childTabs')) {
    const tabNameFather = tabActive.get('tabName');
    const delayChoices = convertArrayFromDropdown(
      options.getIn(['delay'].concat(path)) ? options.getIn(['delay'].concat(path)).toJS() : []
    );
    if (delayChoices.filter((val: any) => val.value === settingsFather.getIn(['delay', 'value'])).length === 0) {
      messages = messages.push(`Wrong value for parameter Delay in ${tabNameFather}`);
    }
    childTabs.map((tab: EditorTabType) => {
      const settings = tab.get('settings');
      const tabNameChild = tab.get('tabName');
      const universeChoices = convertArrayFromDropdown(
        options.getIn(['universe'].concat(path)) ? options.getIn(['universe'].concat(path)).toJS() : []
      );
      if (!checkIfInteger(settings.getIn(['decay', 'value']))) {
        messages = messages.push(`Wrong value for parameter decay in ${tabNameFather} , ${tabNameChild}`);
      }
      if (!checkIfFloat(settings.getIn(['truncation', 'value']))) {
        messages = messages.push(`Wrong value for parameter Truncation in ${tabNameFather} , ${tabNameChild}`);
      }
      if (universeChoices.filter((val: any) => val.value === settings.getIn(['universe', 'value'])).length === 0) {
        messages = messages.push(`Wrong value for parameter Universe in ${tabNameFather}`);
      }
    });
  } else {
    const tabNameFather = tabActive.get('tabName');

    const universePath = ['universe'].concat(path);
    const universeChoices = convertArrayFromDropdown(options.getIn(universePath) ? options.getIn(universePath).toJS() : []);
    const delayPath = ['delay'].concat(path);
    const delayChoices = convertArrayFromDropdown(options.getIn(delayPath) ? options.getIn(delayPath).toJS() : []);
    if (!checkIfInteger(settingsFather.getIn(['decay', 'value']))) {
      messages = messages.push(`Wrong value for parameter decay in ${tabNameFather}`);
    }
    if (!checkIfFloat(settingsFather.getIn(['truncation', 'value']))) {
      messages = messages.push(`Wrong value for parameter Truncation in ${tabNameFather}`);
    }
    if (universeChoices.filter((val: any) => val.value === settingsFather.getIn(['universe', 'value'])).length === 0) {
      messages = messages.push(`Wrong value for parameter Universe in ${tabNameFather}`);
    }
    if (delayChoices.filter((val: any) => val.value === settingsFather.getIn(['delay', 'value'])).length === 0) {
      messages = messages.push(`Wrong value for parameter Delay in ${tabNameFather}`);
    }
  }

  return messages;
};

const getOptionsPlain = (choices: Choices, options: Choice[]): Choice[] => {
  let optionsTotal: Choice[] = [];
  if (Array.isArray(choices)) {
    return options.concat(choices);
  } else {
    Object.keys(choices).forEach((key: string) => {
      optionsTotal = optionsTotal.concat(getOptionsPlain(choices[key], options));
    });

    return optionsTotal;
  }
};

export const getOptionsOrderAndWithoutRepeatedElements = (choices: Choices): DropdownOption[] => {
  const plain = getOptionsPlain(choices, []);

  const deduped = plain.filter(
    (option: Choice, index: number, self: Choice[]) =>
      self.findIndex((property: Choice) => property.value === option.value && property.label === option.label) === index
  );
  const options = convertArrayFromDropdownProperties(deduped);

  return options.sort((propertyA: DropdownOption, propertyB: DropdownOption) =>
    propertyA.key.localeCompare(propertyB.key, undefined, { numeric: true, sensitivity: 'base' })
  );
};
